/**
 * PostImportModal Component
 *
 * A success modal shown after repositories are successfully imported.
 * Guides users to the next logical step - analyzing their portfolio.
 *
 * Features:
 * - Success animation and message
 * - Primary CTA to start analysis (Value Intelligence)
 * - Secondary option to import more repos
 * - Dismiss option for users who want to explore first
 * - Keyboard navigation (Escape to close, Enter to confirm)
 * - Focus trap for accessibility
 *
 * @module components/ui/PostImportModal
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle2,
  Zap,
  Download,
  X,
  Sparkles,
  FolderPlus,
} from 'lucide-react';
import Button from './Button';
import { CreateCollectionModal } from '../collections/CollectionManager';

/**
 * PostImportModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {number} props.importedCount - Number of repositories imported
 * @param {string} props.sessionId - Import session ID for creating collections
 * @param {function} props.onStartAnalysis - Callback when "Start Analysis" is clicked
 * @param {function} props.onImportMore - Callback when "Import More" is clicked
 * @param {function} props.onDismiss - Callback when modal is dismissed
 * @param {function} props.onCollectionCreated - Callback when collection is created
 * @returns {JSX.Element|null}
 */
const PostImportModal = ({
  isOpen,
  importedCount = 0,
  sessionId = null,
  onStartAnalysis,
  onImportMore,
  onDismiss,
  onCollectionCreated,
}) => {
  const dialogRef = useRef(null);
  const primaryButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // State for collection creation modal
  const [showCreateCollection, setShowCreateCollection] = useState(false);

  /**
   * Handle successful collection creation
   */
  const handleCollectionCreated = useCallback((collection) => {
    setShowCreateCollection(false);
    onCollectionCreated?.(collection);
    onDismiss();
  }, [onCollectionCreated, onDismiss]);

  /**
   * Handle escape key to close modal
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    },
    [onDismiss]
  );

  /**
   * Focus management and event listeners
   */
  useEffect(() => {
    if (isOpen) {
      // Store previous focus
      previousActiveElement.current = document.activeElement;

      // Focus the primary button after a short delay for animation
      const timer = setTimeout(() => {
        primaryButtonRef.current?.focus();
      }, 100);

      // Add event listener
      document.addEventListener('keydown', handleKeyDown);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';

        // Restore focus
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, handleKeyDown]);

  /**
   * Handle backdrop click to dismiss
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-import-title"
      aria-describedby="post-import-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        className="relative glass rounded-xl shadow-2xl
                   w-full max-w-md transform transition-all duration-300 ease-out
                   animate-in fade-in zoom-in-95"
      >
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-1 rounded-lg text-gray-400
                     hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close dialog"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 text-center">
          {/* Success Icon with Animation */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="p-4 rounded-full bg-success-500/20 animate-pulse">
                <CheckCircle2 className="w-12 h-12 text-success-400" />
              </div>
              {/* Sparkle decorations */}
              <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-warning-400 animate-bounce" />
            </div>
          </div>

          {/* Title */}
          <h2
            id="post-import-title"
            className="text-2xl font-bold text-white mb-2"
          >
            Import Successful!
          </h2>

          {/* Count Message */}
          <p
            id="post-import-description"
            className="text-gray-400 mb-6"
          >
            <span className="text-success-400 font-semibold">{importedCount}</span>
            {' '}
            {importedCount === 1 ? 'repository has' : 'repositories have'} been imported.
          </p>

          {/* Ready for Analysis */}
          <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-4 mb-6">
            <p className="text-primary-300 font-medium mb-1">
              Ready to analyze your portfolio?
            </p>
            <p className="text-gray-400 text-sm">
              Discover insights about your skills, project relationships, and career opportunities.
            </p>
          </div>

          {/* Primary CTA */}
          <Button
            ref={primaryButtonRef}
            variant="gradient"
            size="lg"
            fullWidth
            onClick={onStartAnalysis}
            icon={Zap}
            className="mb-3"
          >
            Start Analysis
          </Button>

          {/* Save as Collection */}
          {sessionId && (
            <Button
              variant="outline"
              size="md"
              fullWidth
              onClick={() => setShowCreateCollection(true)}
              icon={FolderPlus}
              className="mb-3"
            >
              Save as Collection
            </Button>
          )}

          {/* Secondary Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="md"
              fullWidth
              onClick={onImportMore}
              icon={Download}
            >
              Import More
            </Button>
            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={onDismiss}
            >
              Explore First
            </Button>
          </div>
        </div>
      </div>

      {/* Create Collection Modal */}
      <CreateCollectionModal
        isOpen={showCreateCollection}
        onClose={() => setShowCreateCollection(false)}
        onSuccess={handleCollectionCreated}
        sessionId={sessionId}
      />
    </div>
  );
};

PostImportModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool.isRequired,
  /** Number of repositories that were imported */
  importedCount: PropTypes.number,
  /** Import session ID for creating collections */
  sessionId: PropTypes.string,
  /** Callback when "Start Analysis" button is clicked */
  onStartAnalysis: PropTypes.func.isRequired,
  /** Callback when "Import More" button is clicked */
  onImportMore: PropTypes.func.isRequired,
  /** Callback when modal is dismissed (close button, backdrop, or "Explore First") */
  onDismiss: PropTypes.func.isRequired,
  /** Callback when collection is created from import */
  onCollectionCreated: PropTypes.func,
};

export default PostImportModal;
